import {
  type GetEnquiryDetailsResponseDto,
  GetEnquiryDetailsResponseDtoEnquiryTypeEnum,
  MyEnquiryListItemDtoTypeOfEnquiryEnum,
} from "@unreserved/sdk";
import { getOpenInspectionTimeSlots } from "@unreserved/utils";

import type { EnquiryStatusValue, EnquiryType } from "./enquiry-types";

function toStr(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = typeof v === "string" ? v : String(v);
  const t = s.trim();
  return t === "" ? undefined : t;
}

export interface EnquirerContact {
  name?: string;
  email?: string;
  phone?: string;
}

/** Display labels for each enquiry type (single source of truth for card title / subject) */
export const ENQUIRY_TYPE_LABELS: Record<EnquiryType, string> = {
  BOOK_INSPECTION: "Book Inspection",
  LIST_PROPERTY: "List Property",
  PROPERTY_VALUATION: "Property Valuation",
  REQUEST_DOCUMENTS: "Request Documents",
  GENERAL_QUERIES: "General Queries",
  CONTACT_UNRESERVED: "Contact Unreserved",
  AVAILABLE_PROPERTIES: "Available Properties",
} as const;

export function getEnquiryTypeLabel(enquiryType: EnquiryType): string {
  return ENQUIRY_TYPE_LABELS[enquiryType];
}

/** Enquiry status options for Select and badge styling */
export const ENQUIRY_STATUS_OPTIONS = ["OPEN", "IN PROGRESS", "RESOLVED"] as const;

export const ENQUIRY_STATUS_BADGE_CLASS: Record<EnquiryStatusValue, string> = {
  OPEN: "bg-info-background hover:!bg-info-background border border-info-300 !text-info-800",
  "IN PROGRESS":
    "bg-warning-background hover:!bg-warning-background border border-warning-300 !text-warning-800",
  RESOLVED: "bg-success-100 hover:!bg-success-100 !text-success-600",
};

export function normalizeEnquiryStatus(value: string): EnquiryStatusValue {
  const upper = value.toUpperCase().replace(/_/g, " ");
  if (upper === "IN PROGRESS") return "IN PROGRESS";
  if (upper === "RESOLVED") return "RESOLVED";
  return "OPEN";
}

export function getEnquiryStatusBadgeClass(status: string): string {
  return ENQUIRY_STATUS_BADGE_CLASS[normalizeEnquiryStatus(status)];
}

/** Map list API typeOfEnquiry (DTO display strings) to UI EnquiryType. Keys from SDK so we stay in sync with API. */
const LIST_TYPE_TO_ENQUIRY_TYPE: Record<MyEnquiryListItemDtoTypeOfEnquiryEnum, EnquiryType> = {
  [MyEnquiryListItemDtoTypeOfEnquiryEnum.GeneralQueries]: "GENERAL_QUERIES",
  [MyEnquiryListItemDtoTypeOfEnquiryEnum.RequestDocuments]: "REQUEST_DOCUMENTS",
  [MyEnquiryListItemDtoTypeOfEnquiryEnum.GetValuation]: "PROPERTY_VALUATION",
  [MyEnquiryListItemDtoTypeOfEnquiryEnum.BookAnInspection]: "BOOK_INSPECTION",
  [MyEnquiryListItemDtoTypeOfEnquiryEnum.ListYourProperty]: "LIST_PROPERTY",
  [MyEnquiryListItemDtoTypeOfEnquiryEnum.PropertyLeads]: "LIST_PROPERTY",
};

export function mapListTypeOfEnquiryToEnquiryType(
  typeOfEnquiry: MyEnquiryListItemDtoTypeOfEnquiryEnum,
): EnquiryType {
  return LIST_TYPE_TO_ENQUIRY_TYPE[typeOfEnquiry];
}

/** Map enquiry details API enquiryType (snake_case) to UI EnquiryType. Keys from SDK so we stay in sync with API. */
const DETAILS_TYPE_TO_ENQUIRY_TYPE: Record<
  GetEnquiryDetailsResponseDtoEnquiryTypeEnum,
  EnquiryType
> = {
  [GetEnquiryDetailsResponseDtoEnquiryTypeEnum.GeneralEnquiry]: "GENERAL_QUERIES",
  [GetEnquiryDetailsResponseDtoEnquiryTypeEnum.DocumentRequest]: "REQUEST_DOCUMENTS",
  [GetEnquiryDetailsResponseDtoEnquiryTypeEnum.PropertyValuation]: "PROPERTY_VALUATION",
  [GetEnquiryDetailsResponseDtoEnquiryTypeEnum.Inspection]: "BOOK_INSPECTION",
  [GetEnquiryDetailsResponseDtoEnquiryTypeEnum.ListProperty]: "LIST_PROPERTY",
  [GetEnquiryDetailsResponseDtoEnquiryTypeEnum.ListingCrmEnquiry]: "LIST_PROPERTY",
};

export function mapDetailsEnquiryTypeToEnquiryType(
  apiType: GetEnquiryDetailsResponseDtoEnquiryTypeEnum,
): EnquiryType {
  return DETAILS_TYPE_TO_ENQUIRY_TYPE[apiType];
}

const LIST_VALUES = Object.values(MyEnquiryListItemDtoTypeOfEnquiryEnum) as string[];
const DETAILS_VALUES = Object.values(GetEnquiryDetailsResponseDtoEnquiryTypeEnum) as string[];

export function buildTimeSlots(): { id: string; label: string }[] {
  return getOpenInspectionTimeSlots({ labelStyle: "padded" }).map(({ id, label }) => ({
    id,
    label,
  }));
}

export function parseTimeSlotId(id: string): [number, number] | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(id);
  if (!match) return null;
  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return [hour, minute];
}

export function typeOfEnquiryFromApi(value: string | null | undefined): EnquiryType | undefined {
  if (value == null || value === "") return undefined;
  if (LIST_VALUES.includes(value))
    return LIST_TYPE_TO_ENQUIRY_TYPE[value as MyEnquiryListItemDtoTypeOfEnquiryEnum];
  if (DETAILS_VALUES.includes(value))
    return DETAILS_TYPE_TO_ENQUIRY_TYPE[value as GetEnquiryDetailsResponseDtoEnquiryTypeEnum];
  return undefined;
}

export function getEnquirerContact(
  data: GetEnquiryDetailsResponseDto | null | undefined,
): EnquirerContact | null {
  if (!data) return null;

  switch (data.enquiryType) {
    case GetEnquiryDetailsResponseDtoEnquiryTypeEnum.GeneralEnquiry: {
      const email = toStr(data.generalEnquiry?.email);
      if (!email) return null;
      return { name: toStr(data.generalEnquiry?.name), email };
    }
    case GetEnquiryDetailsResponseDtoEnquiryTypeEnum.DocumentRequest: {
      const email = toStr(data.documentRequests?.email);
      if (!email) return null;
      return { email };
    }
    case GetEnquiryDetailsResponseDtoEnquiryTypeEnum.Inspection: {
      const regs = data.registeredInspections as
        | { name?: unknown; email?: unknown; phoneNumber?: unknown; countryCode?: unknown }
        | { name?: unknown; email?: unknown; phoneNumber?: unknown; countryCode?: unknown }[]
        | null
        | undefined;
      const first = Array.isArray(regs) ? regs[0] : regs;
      const email = toStr(first?.email);
      if (!email) return null;
      const rawPhone = toStr(first?.phoneNumber);
      const countryCode = toStr(first?.countryCode);
      const phone =
        rawPhone && countryCode ? `${countryCode} ${rawPhone}`.trim() : (rawPhone ?? countryCode);
      return { name: toStr(first?.name), email, phone };
    }
    case GetEnquiryDetailsResponseDtoEnquiryTypeEnum.ListingCrmEnquiry: {
      const leads = data.propertyLeads as
        | { personName?: unknown; name?: unknown; email?: unknown; phoneNumber?: unknown }
        | null
        | undefined;
      if (!leads) return null;
      const email = toStr(leads.email);
      if (!email) return null;
      return {
        name: toStr(leads.personName ?? leads.name),
        email,
        phone: toStr(leads.phoneNumber),
      };
    }
    case GetEnquiryDetailsResponseDtoEnquiryTypeEnum.PropertyValuation: {
      const pv = data.propertyValuationRequests as
        | { name?: unknown; email?: unknown; phoneNumber?: unknown; countryCode?: unknown }
        | null
        | undefined;
      if (!pv) return null;
      const email = toStr(pv.email);
      if (!email) return null;
      const rawPhone = toStr(pv.phoneNumber);
      const countryCode = toStr(pv.countryCode);
      const phone =
        rawPhone && countryCode ? `${countryCode} ${rawPhone}`.trim() : (rawPhone ?? countryCode);
      return { name: toStr(pv.name), email, phone };
    }
    case GetEnquiryDetailsResponseDtoEnquiryTypeEnum.ListProperty: {
      const lp = data.listPropertySummary;
      if (!lp) return null;
      const phone = toStr(lp.phoneNumber);
      if (!phone) return null;
      return { phone };
    }
    default:
      return null;
  }
}
